import { Formik, ErrorMessage, Form } from 'formik';
import React from 'react';
import { ResetPasswordModel } from '../../models/resetPasswordModel';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { Typography, Link, CssBaseline, Container, Grid } from '@material-ui/core';
import { useStyles } from './style';
import { validationSchema } from './validation';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import { resetPassword, forgotPassword } from '../../api/authApi';
import { notifySuccess } from '../../stores/notification/actions';
import { AppThunkDispatch, ApplicationState } from '../../stores';
import { useDispatch, useSelector } from 'react-redux';
import ZTSHLogo from '../../images/ztsh-logo.png';

const ResetPassword = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isLoading = useSelector<ApplicationState, boolean>(state => state.loader.loading);
  const { t } = useTranslation();
  const { email } = useParams();

  const handleResetPassword = async (values: ResetPasswordModel) => {
    const response = await resetPassword(email!, values.password, values.code);
    if (response) {
      history.replace('/login');
    }
  };

  const handleResendCode = async () => {
    const response = await forgotPassword(email!);
    if (response) {
      dispatch(notifySuccess(t('resend_code_message')));
    }
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <div className={classes.paper}>
        <img className={classes.logo} src={ZTSHLogo} alt={'ZTSH logo'} />
        <Typography component='h1' variant='h5'>
          {t('reset_password')}
        </Typography>
        <Formik
          initialValues={{ code: '', password: '', confirmPassword: '' }}
          validationSchema={validationSchema}
          validateOnBlur={true}
          onSubmit={handleResetPassword}
        >
          {({ handleChange, values, errors, touched, isValid }) => (
            <Form className={classes.form}>
              <TextField
                variant='outlined'
                margin='normal'
                required
                fullWidth
                id='code'
                label={t('code')}
                name='code'
                autoFocus
                onChange={handleChange}
                value={values.code}
              />
              {errors.code && touched.code ? <div>{errors.code}</div> : null}
              <ErrorMessage name='code'>
                {(msg: React.ReactNode) => <div className='error error-message'>{msg}</div>}
              </ErrorMessage>
              <TextField
                variant='outlined'
                margin='normal'
                required
                fullWidth
                id='password'
                label={t('password')}
                name='password'
                type='password'
                onChange={handleChange}
                value={values.password}
              />
              {errors.password && touched.password ? <div>{errors.password}</div> : null}
              <ErrorMessage name='password'>
                {(msg: React.ReactNode) => <div className='error error-message'>{msg}</div>}
              </ErrorMessage>
              <TextField
                variant='outlined'
                margin='normal'
                required
                fullWidth
                id='confirmPassword'
                label={t('confirm_password')}
                name='confirmPassword'
                type='password'
                onChange={handleChange}
                value={values.confirmPassword}
              />
              {errors.confirmPassword && touched.confirmPassword ? (
                <div>{errors.confirmPassword}</div>
              ) : null}
              <ErrorMessage name='confirmPassword'>
                {(msg: React.ReactNode) => <div className='error error-message'>{msg}</div>}
              </ErrorMessage>
              <Button
                type='submit'
                fullWidth
                variant='contained'
                color='primary'
                disabled={!isValid || isLoading}
              >
                {t('reset_password')}
              </Button>
              <Grid container style={{ marginTop: 10 }}>
                <Grid item xs>
                  <Button
                    variant='text'
                    fullWidth
                    color='primary'
                    type='button'
                    onClick={handleResendCode}
                  >
                    {t('resend_code')}
                  </Button>
                </Grid>
              </Grid>
              <Grid container justify='flex-end' style={{ marginTop: 10 }}>
                <Grid item xs>
                  <Link variant='body2' component={RouterLink} to='/login'>
                    {t('signin')}
                  </Link>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </div>
    </Container>
  );
};

export default ResetPassword;
